import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandedCard, BootPulse } from '../components';
import { useTenantContext } from '../hooks/useBranding';
import { fetchMe, MeResponse } from '../services/meService';
import { kratosService, mfaPath } from '../services/kratosService';
import './Home.css';

/**
 * The orientation page. Signing in used to drop you straight onto /settings
 * with no sense of what this app is for or where anything else lives — this
 * answers "what can I do here?" and, for operators only, points at the
 * separate admin console where tenants are managed.
 */
export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { context } = useTenantContext();
  const [me, setMe] = useState<MeResponse | null>(null);
  // Sign-in already requires a verified address, so this is only true after
  // an email change in settings; the verify tile shows only then.
  const [unverified, setUnverified] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      // Kratos is the authority on "is there a session". The control plane is
      // consulted only for roles/capabilities — if it is unreachable we still
      // render the page rather than bouncing to /login, which would ping-pong
      // against a login page that correctly says "you are already signed in".
      const { session, secondFactorRequired } = await kratosService.sessionState();
      if (!active) return;
      if (secondFactorRequired) {
        navigate(mfaPath('/'));
        return;
      }
      if (!session) {
        navigate('/login?return_to=/');
        return;
      }
      const addresses = (
        session.identity as { verifiable_addresses?: Array<{ verified?: boolean }> } | undefined
      )?.verifiable_addresses;
      setUnverified(!!addresses?.some((address) => !address.verified));

      const result = await fetchMe();
      if (!active) return;
      setMe(
        result.authenticated
          ? result
          : {
              authenticated: true,
              identity: {
                id: '',
                email: (session.identity?.traits as { email?: string } | undefined)?.email,
              },
            }
      );
    };

    void load();
    return () => {
      active = false;
    };
  }, [navigate]);

  if (!me) {
    return (
      <BrandedCard>
        <BootPulse label="Loading your account" />
      </BrandedCard>
    );
  }

  const displayName = me.identity?.name || me.identity?.email || 'your account';

  return (
    <BrandedCard title="Your Account">
      <p className="home-intro">
        Signed in as <span className="home-identity">{displayName}</span>
        {context?.name ? <> on <strong>{context.name}</strong></> : null}.
      </p>

      <div className="home-grid">
        <a href="/settings" className="home-tile">
          <h3>Profile &amp; password</h3>
          <p>Update your name, email and phone, or change your password.</p>
        </a>

        {unverified && (
          <a href="/verification" className="home-tile">
            <h3>Verify your email</h3>
            <p>Your address isn&apos;t confirmed yet. Confirm it so you can sign in and recover your account.</p>
          </a>
        )}

        <a href="/logout" className="home-tile">
          <h3>Sign out</h3>
          <p>End this session on this device.</p>
        </a>

        {me.is_operator && me.admin_console_url && (
          <a href={me.admin_console_url} className="home-tile home-tile--operator">
            <h3>Admin console →</h3>
            <p>
              Manage tenants, OAuth2 clients, identities and permissions. Opens the
              separate operator console; each area asks you to confirm your password.
            </p>
            <p>
              The console listens only on the server&apos;s loopback interface: open an SSH
              tunnel first (<span className="home-mono">ssh -L 3002:127.0.0.1:3002 &lt;server&gt;</span>).
            </p>
          </a>
        )}
      </div>

      {me.is_operator && (
        <p className="home-note">
          You hold the <span className="home-mono">{me.operator_tenant}</span> operator
          role — tenant management lives in the admin console, not here.
        </p>
      )}
    </BrandedCard>
  );
};
